/**
 * 项目表格视图组件
 * 以表格形式展示项目列表，支持排序和快速操作
 * 设计理念: 清晰的表头层级、统一的数据行样式、聚焦的操作按钮
 */
import React from 'react';
import { Table, Tag, Progress, Space, Button, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  ExportOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { DashboardService } from '../../services/dashboardService';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Text } = Typography;

// 统一配色系统
const TABLE_THEME = {
  headerBg: 'rgba(210, 218, 224, 0.9)',
  headerColor: '#41535f',
  rowBg: 'rgba(224, 230, 234, 0.86)',
  rowHoverBg: 'rgba(236, 240, 243, 0.92)',
  borderColor: 'rgba(16, 27, 35, 0.08)',
  textPrimary: '#10171d',
  textSecondary: '#455763',
  textMuted: '#667986',
  accentGold: '#b99258',
  accentBlue: '#547590',
  shadow: '0 18px 40px rgba(46, 60, 70, 0.12)',
};

const getDaysRemainingLabel = (project: ProjectOverviewData) => {
  if (!project.endDate) {
    return '未设置结束日期';
  }

  if (project.daysRemaining > 0) {
    return `剩余 ${project.daysRemaining} 天`;
  }

  if (project.daysRemaining === 0) {
    return '今日到期';
  }

  return `已超期 ${Math.abs(project.daysRemaining)} 天`;
};

export interface ProjectTableViewProps {
  projects: ProjectOverviewData[];
  loading?: boolean;
  onViewDetails?: (project: ProjectOverviewData) => void;
  onExport?: (project: ProjectOverviewData) => void;
  onAddData?: (project: ProjectOverviewData) => void;
  isMobile?: boolean;
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin />, text: '进行中' },
  '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined />, text: '待开始' },
  '已暂停': { color: 'warning', icon: <PauseCircleOutlined />, text: '已暂停' }
};

export const ProjectTableView: React.FC<ProjectTableViewProps> = ({
  projects,
  loading = false,
  onViewDetails,
  onExport,
  onAddData,
  isMobile = false
}) => {
  const formatMoney = (value: number): string => {
    if (value >= 10000) {
      return `¥${(value / 10000).toFixed(1)}万`;
    }
    return `¥${value.toLocaleString()}`;
  };

  const desktopColumns: ColumnsType<ProjectOverviewData> = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record) => (
        <Tooltip title={name}>
          <div>
            <Text strong style={{ fontSize: 13, color: TABLE_THEME.textPrimary }}>{name}</Text>
            <div style={{ fontSize: 11, color: TABLE_THEME.textMuted, marginTop: 3 }}>
              {(record as any).projectCode || '未设置编码'}
            </div>
          </div>
        </Tooltip>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG['待开始'];
        return (
          <Tag
            color={config.color}
            icon={config.icon}
            style={{
              fontSize: 11,
              borderRadius: 999,
              marginRight: 0,
              background: config.color === 'processing' ? 'rgba(84, 117, 144, 0.14)' :
                         config.color === 'success' ? 'rgba(101, 214, 184, 0.14)' :
                         config.color === 'warning' ? 'rgba(246, 196, 83, 0.14)' :
                         'rgba(102, 121, 134, 0.12)',
              border: 'none'
            }}
          >
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: '预算燃耗',
      key: 'budget',
      width: 220,
      sorter: (a, b) => {
        const aPercent = a.budget > 0 ? a.actualCost / a.budget : 0;
        const bPercent = b.budget > 0 ? b.actualCost / b.budget : 0;
        return aPercent - bPercent;
      },
      render: (_, record) => {
        const health = DashboardService.calculateProjectHealth(record.budget, record.actualCost);
        const budgetColors = getBudgetBasedColors(record.budget, record.actualCost);
        const usageText = record.budget > 0 ? `${health.percentage.toFixed(1)}%` : '未设预算';
        return (
          <Tooltip title={`预算状态: ${budgetColors.statusText}`}>
            <div style={{ minWidth: 180 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text style={{ color: TABLE_THEME.textSecondary, fontSize: 12 }}>预算燃耗</Text>
                <Text style={{ color: health.color, fontSize: 12, fontWeight: 700 }}>{usageText}</Text>
              </div>
              <Progress
                percent={Math.min(health.percentage, 100)}
                size="small"
                strokeColor={health.color}
                railColor="rgba(16, 27, 35, 0.08)"
                showInfo={false}
              />
              <div style={{ fontSize: 11, color: TABLE_THEME.textMuted, marginTop: 6 }}>
                成本 {formatMoney(record.actualCost)} / 预算 {record.budget > 0 ? formatMoney(record.budget) : '未设置'}
              </div>
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: '项目进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ color: TABLE_THEME.textSecondary, fontSize: 12 }}>完成度</Text>
            <Text style={{ color: TABLE_THEME.textPrimary, fontSize: 12, fontWeight: 600 }}>{progress}%</Text>
          </div>
          <Progress
            percent={progress}
            size="small"
            strokeColor={progress >= 100 ? '#52c41a' : TABLE_THEME.accentBlue}
            railColor="rgba(16, 27, 35, 0.08)"
            showInfo={false}
          />
        </div>
      )
    },
    {
      title: '工期状态',
      key: 'daysRemaining',
      width: 120,
      render: (_, record) => (
        <Text style={{ color: (record.daysRemaining ?? 0) <= 0 ? '#ff4d4f' : TABLE_THEME.textSecondary, fontSize: 12 }}>
          {record.status === '已完成' ? '-' : getDaysRemainingLabel(record)}
        </Text>
      )
    },
    {
      title: '最近更新',
      dataIndex: 'recentActivity',
      key: 'recentActivity',
      width: 180,
      ellipsis: true,
      render: (activity: string) => (
        <Tooltip title={activity}>
          <Text style={{ color: TABLE_THEME.textMuted, fontSize: 12 }}>{activity || '暂无记录'}</Text>
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails?.(record)}
              style={{ color: TABLE_THEME.textSecondary }}
            />
          </Tooltip>
          <Tooltip title="导出">
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={() => onExport?.(record)}
              style={{ color: TABLE_THEME.textSecondary }}
            />
          </Tooltip>
          <Tooltip title="添加数据">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onAddData?.(record)}
              style={{ color: TABLE_THEME.accentGold }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const mobileColumns: ColumnsType<ProjectOverviewData> = [
    {
      title: '项目',
      key: 'project',
      render: (_, record) => {
        const health = DashboardService.calculateProjectHealth(record.budget, record.actualCost);
        const statusConfig = STATUS_CONFIG[record.status] || STATUS_CONFIG['待开始'];
        return (
          <div style={{ padding: '4px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Text strong style={{ fontSize: 14, color: TABLE_THEME.textPrimary }}>{record.name}</Text>
              <Tag
                color={statusConfig.color}
                style={{
                  fontSize: 10,
                  marginRight: 0,
                  borderRadius: 999,
                  background: statusConfig.color === 'processing' ? 'rgba(84, 117, 144, 0.14)' :
                             statusConfig.color === 'success' ? 'rgba(101, 214, 184, 0.14)' :
                             statusConfig.color === 'warning' ? 'rgba(246, 196, 83, 0.14)' :
                             'rgba(102, 121, 134, 0.12)',
                  border: 'none'
                }}
              >
                {statusConfig.text}
              </Tag>
            </div>
            <Progress
              percent={record.progress}
              size="small"
              strokeColor={record.progress >= 100 ? '#52c41a' : TABLE_THEME.accentBlue}
              railColor="rgba(16, 27, 35, 0.08)"
              style={{ marginBottom: 6 }}
              showInfo={false}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: TABLE_THEME.textMuted, gap: 12 }}>
              <span style={{ color: health.color }}>成本 {formatMoney(record.actualCost)}</span>
              <span>{getDaysRemainingLabel(record)}</span>
            </div>
          </div>
        );
      }
    },
  ];

  return (
    <div
      style={{
        borderRadius: 24,
        overflow: 'hidden',
        background: TABLE_THEME.rowBg,
        border: TABLE_THEME.borderColor,
        boxShadow: TABLE_THEME.shadow,
        padding: isMobile ? 8 : 10
      }}
    >
      <Table
        columns={isMobile ? mobileColumns : desktopColumns}
        dataSource={projects}
        loading={loading}
        rowKey={(record) => record.id || record._id || record.name}
        size={isMobile ? 'small' : 'middle'}
        pagination={false}
        scroll={isMobile ? undefined : { x: 1100 }}
        style={{ background: 'transparent' }}
        onRow={(record) => ({
          style: {
            background: 'transparent',
            transition: 'background-color 0.2s'
          }
        })}
      />
    </div>
  );
};

export default ProjectTableView;
